import React, { useEffect } from 'react';
import {
  Alert,
  Bullseye,
  ClipboardCopy,
  Spinner,
  Stack,
  StackItem,
  Text,
  TextContent,
} from '@patternfly/react-core';
import { useWizardContext } from '@patternfly/react-core';
import { CheckCircleIcon } from '@patternfly/react-icons';
import { useStableAccessToken } from '@app/GranularDeploy/hooks/useStableAccessToken';
import type { SessionStatusRecord } from '../helpers/apiHelpers';
import { apiTriggerRegressionTest, RegressionTestSuite } from '../helpers/apiHelpers';
import { RegressionTestSuiteSelection } from './RegressionTestSuiteSelector';

type SessionStatus = SessionStatusRecord['status'] | 'timeout' | null;

export type SuiteTriggerState =
  | { status: 'triggering' }
  | { status: 'triggered'; jobUrl: string }
  | { status: 'queued'; id: number }
  | { status: 'error'; message: string };

interface DeployStepProps {
  isDeploying: boolean;
  jobUrl: string | null;
  deployError: string | null;
  statusMessage: string;
  previewTag: string;
  sessionStatus: SessionStatus;
  namespace: string;
  environment: string;
  deployedServiceNames: string[];
  regressionTestsEnabled: boolean;
  regressionTestSelections: Record<string, RegressionTestSuiteSelection>;
  regressionTriggerStarted: boolean;
  onRegressionTriggerStarted: () => void;
  regressionTriggerResults: Record<string, SuiteTriggerState>;
  onRegressionTriggerResultsChange: (
    update: (prev: Record<string, SuiteTriggerState>) => Record<string, SuiteTriggerState>,
  ) => void;
}

export const DeployStep: React.FC<DeployStepProps> = ({
  isDeploying,
  jobUrl,
  deployError,
  statusMessage,
  previewTag,
  sessionStatus,
  namespace,
  environment,
  deployedServiceNames,
  regressionTestsEnabled,
  regressionTestSelections,
  regressionTriggerStarted,
  onRegressionTriggerStarted,
  regressionTriggerResults,
  onRegressionTriggerResultsChange,
}) => {
  const routingHeader = `Baggage: mirrord-session=${previewTag}`;
  const tokenGetter = useStableAccessToken();
  const { goToStepById } = useWizardContext();

  const isLive = sessionStatus === 'running';

  useEffect(() => {
    if (!isLive || !regressionTestsEnabled || regressionTriggerStarted) return;
    onRegressionTriggerStarted();

    const enabledSuites = Object.entries(regressionTestSelections).filter(([, s]) => s.enabled);

    onRegressionTriggerResultsChange((prev) => ({
      ...prev,
      ...Object.fromEntries(enabledSuites.map(([key]) => [key, { status: 'triggering' as const }])),
    }));

    for (const [suiteKey, selection] of enabledSuites) {
      apiTriggerRegressionTest(
        {
          suite: suiteKey as RegressionTestSuite,
          namespace,
          environment,
          previewKey: previewTag,
          services: deployedServiceNames,
          ...(Object.keys(selection.paramOverrides).length ? { paramOverrides: selection.paramOverrides } : {}),
        },
        tokenGetter,
      )
        .then((result) => {
          onRegressionTriggerResultsChange((prev) => ({ ...prev, [suiteKey]: result }));
        })
        .catch((err: any) => {
          onRegressionTriggerResultsChange((prev) => ({
            ...prev,
            [suiteKey]: { status: 'error', message: err?.message ?? 'Failed to trigger regression test' },
          }));
        });
    }

    goToStepById('preview-step-testing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive]);

  if (isDeploying) {
    return (
      <Bullseye>
        <Stack hasGutter>
          <StackItem>
            <Spinner size="xl" />
          </StackItem>
          <StackItem>
            <TextContent>
              <Text>{statusMessage || 'Deploying preview environment…'}</Text>
            </TextContent>
          </StackItem>
        </Stack>
      </Bullseye>
    );
  }

  if (deployError) {
    return (
      <Alert variant="danger" title="Deploy failed" isInline>
        {deployError}
      </Alert>
    );
  }

  if (jobUrl) {
    const isFailed = sessionStatus === 'failed';
    const isTimeout = sessionStatus === 'timeout';
    const isPending = !sessionStatus || sessionStatus === 'pending';

    return (
      <Stack hasGutter>
        <StackItem>
          {isLive && (
            <Alert
              variant="success"
              title="Preview environment is live"
              isInline
              customIcon={<CheckCircleIcon />}
            />
          )}
          {isFailed && (
            <Alert variant="danger" title="Deploy failed" isInline>
              The Jenkins job reported a failure. Check the job link below for details.
            </Alert>
          )}
          {isTimeout && (
            <Alert variant="warning" title="Taking longer than expected" isInline>
              The preview pod hasn't confirmed as live yet. If the Jenkins job succeeded it may still be starting — check the job link below.
            </Alert>
          )}
          {isPending && (
            <Bullseye>
              <Stack hasGutter>
                <StackItem isFilled={false} style={{ textAlign: 'center' }}>
                  <Spinner size="lg" />
                </StackItem>
                <StackItem>
                  <TextContent>
                    <Text>Jenkins job running — waiting for preview pod to start…</Text>
                  </TextContent>
                </StackItem>
              </Stack>
            </Bullseye>
          )}
        </StackItem>

        <StackItem>
          <TextContent>
            <Text component="h3">Jenkins Job</Text>
          </TextContent>
          <ClipboardCopy isReadOnly hoverTip="Copy" clickTip="Copied">
            {jobUrl}
          </ClipboardCopy>
        </StackItem>

        <StackItem>
          <TextContent>
            <Text component="h3">Routing Header</Text>
            <Text>
              Paste this header into your browser extension, Selenium config, or curl command to
              route requests through the preview overlay:
            </Text>
          </TextContent>
          <ClipboardCopy isReadOnly hoverTip="Copy" clickTip="Copied">
            {routingHeader}
          </ClipboardCopy>
        </StackItem>
      </Stack>
    );
  }

  return (
    <TextContent>
      <Text>Click <strong>Deploy</strong> to trigger the preview environment.</Text>
    </TextContent>
  );
};
